// conversationDisplayTitle.js

const normalizeKey = (value) => String(value || '').trim();

const getMemberDisplayName = (memberKey, membersByKey = {}) => {
  const key = normalizeKey(memberKey);
  if (!key) return 'Unknown member';

  const profile = membersByKey[key];
  const candidates = [
    profile?.name,
    profile?.data_profile?.name,
    profile?.title,
    profile?.email,
  ];

  const valid = candidates.find((candidate) => {
    const text = String(candidate || '').trim();
    return text && text !== key;
  });

  if (valid) return String(valid).trim();

  const email = String(profile?.email || '').trim();
  if (email.includes('@')) {
    return email.split('@')[0];
  }

  return 'Unknown member';
};

/**
 * Builds the sidebar/toolbar label for a conversation with implied "You".
 *
 * @param {object} options - Label options.
 * @param {string} options.currentUserKey - Active user key.
 * @param {string[]} options.memberKeys - Conversation participant keys.
 * @param {object} options.membersByKey - Base member profiles keyed by user key.
 * @returns {string} Display label.
 */
export function buildConversationDisplayTitle({
  currentUserKey,
  memberKeys = [],
  membersByKey = {},
} = {}) {
  const current = normalizeKey(currentUserKey);
  const keys = (Array.isArray(memberKeys) ? memberKeys : [])
    .map((key) => normalizeKey(key))
    .filter(Boolean);

  const others = keys
    .filter((key) => key !== current)
    .map((key) => ({
      key,
      name: getMemberDisplayName(key, membersByKey),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (others.length === 0) {
    return 'Notes to self';
  }

  if (others.length === 1) {
    return others[0].name;
  }

  if (others.length === 2) {
    return `${others[0].name} and ${others[1].name}`;
  }

  return `${others[0].name} and ${others.length - 1} Others`;
}
